import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  AfterViewInit,
  OnDestroy,
  SecurityContext,
  input,
  inject,
  viewChild,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { DrawerService } from 'src/app/services/drawer.service';
import { DocumentationPage } from 'src/app/types/documentation';
import { SessionService } from 'src/app/services/session.service';

/** Documentation is authored for the marketing site, so relative links resolve against it. */
const DOCUMENTATION_SITE_URL = 'https://budgets.money/';
/** The only host that receives the session token on a documentation link. */
const JWT_LINK_HOST = 'browser.budgets.money';

function parseUrl(href: string, base?: string): URL | null {
  try {
    return new URL(href, base);
  } catch {
    return null;
  }
}

@Component({
  selector: 'documentation',
  templateUrl: './documentation.component.html',
  styleUrl: './documentation.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DocumentationComponent implements AfterViewInit, OnDestroy {
  private drawerService = inject(DrawerService);
  private session = inject(SessionService);
  private sanitizer = inject(DomSanitizer);

  readonly documentationRef = viewChild.required<ElementRef<Element>>('documentation');
  content = input.required<DocumentationPage>();

  links?: NodeListOf<HTMLAnchorElement>;
  private readonly boundOnLinkClick = this.onLinkClick.bind(this);

  ngAfterViewInit() {
    const documentationElement = this.documentationRef().nativeElement;
    // The HTML comes from the remote docs API; sanitize it like an [innerHTML] binding would.
    documentationElement.innerHTML = this.sanitizer.sanitize(SecurityContext.HTML, this.content().html) ?? '';
    this.setupLinks(documentationElement);
  }

  setupLinks(documentationElement: Element) {
    this.links = documentationElement.querySelectorAll('a');
    this.links.forEach(a => {
      a.addEventListener('click', this.boundOnLinkClick);
      const href = a.getAttribute('href');
      if (!href || href.startsWith('#')) return;
      const resolved = parseUrl(href, DOCUMENTATION_SITE_URL);
      if (resolved) a.href = this.addJWTToLinks(resolved.toString());
    });
  }

  addJWTToLinks(href: string): string {
    const url = parseUrl(href);
    // Match the exact host over HTTPS: a substring check would also send the token to
    // look-alikes such as https://example.com/?next=browser.budgets.money.
    if (url?.protocol !== 'https:' || url.hostname !== JWT_LINK_HOST) return href;
    const jwt = this.session.storedJwt;
    if (!jwt) return href;
    url.searchParams.set('jwt', jwt);
    return url.toString();
  }

  onLinkClick(event: MouseEvent): void {
    const target = event.currentTarget as HTMLAnchorElement | null;
    if (target == null) return;

    const url = parseUrl(target.href);
    if (!url) return;
    if (url.hostname !== 'budgets.money' && url.hostname !== 'www.budgets.money') return;

    // Required to prevent href
    event.preventDefault();

    const slug = url.pathname.split('/').filter(Boolean)[0];
    if (slug == null) return;

    this.drawerService.openDocumentationDrawer(slug);
  }

  ngOnDestroy() {
    this.links?.forEach(a => a.removeEventListener('click', this.boundOnLinkClick));
  }
}
